import React, { useEffect, useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useTranslations } from '../hooks/useTranslations';
import { LegalModal } from './LegalModal';
import { PRIVACY_POLICY, TERMS_CONDITIONS, REFUND_POLICY } from '../data/legalContent';
export function Footer() {
  const {
    t
  } = useTranslations();
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | 'refund' | null>(null);
  useEffect(() => {
    const handleOpenModal = (e: Event) => {
      const customEvent = e as CustomEvent;
      setActiveModal(customEvent.detail as 'privacy' | 'terms' | 'refund');
    };
    window.addEventListener('openLegalModal', handleOpenModal);
    return () => window.removeEventListener('openLegalModal', handleOpenModal);
  }, []);
  return <>
      <footer className="bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 text-white pt-16 pb-8" data-id="element-112">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-113">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12" data-id="element-114">
            {/* Brand */}
            <div className="col-span-1 lg:col-span-1" data-id="element-115">
              <div className="flex items-center mb-6" data-id="element-116">
                <img src="https://cdn.magicpatterns.com/uploads/cw4DwFLznDxNXMB79xGgQt/Asset_21x.png" alt="Brixen Credit" className="h-8 w-auto brightness-0 invert" data-id="element-117" />
              </div>
              <p className="text-purple-200 text-sm mb-6" data-id="element-118">
                {t('footer.description')}
              </p>
            </div>

            {/* Quick Links */}
            <div data-id="element-119">
              <h4 className="text-lg font-bold mb-6" data-id="element-120">
                {t('footer.quickLinks')}
              </h4>
              <ul className="space-y-3 text-sm text-purple-200" data-id="element-121">
                <li data-id="element-122">
                  <a href="#" className="hover:text-white transition-colors" data-id="element-123">
                    {t('nav.home')}
                  </a>
                </li>
                <li data-id="element-124">
                  <a href="#how-it-works" className="hover:text-white transition-colors" data-id="element-125">
                    {t('nav.howItWorks')}
                  </a>
                </li>
                <li data-id="element-126">
                  <a href="#pricing" className="hover:text-white transition-colors" data-id="element-127">
                    {t('nav.pricing')}
                  </a>
                </li>
                <li data-id="element-128">
                  <a href="#contact" className="hover:text-white transition-colors" data-id="element-129">
                    {t('nav.contact')}
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div data-id="element-130">
              <h4 className="text-lg font-bold mb-6" data-id="element-131">{t('footer.contact')}</h4>
              <ul className="space-y-4 text-sm text-purple-200" data-id="element-132">
                <li className="flex items-start" data-id="element-133">
                  <MapPin className="w-5 h-5 mr-2 flex-shrink-0 text-purple-400" data-id="element-134" />
                  <span data-id="element-135">
                    390 NE 191ST ST STE 8123
                    <br data-id="element-136" />
                    Miami, FL 33179
                  </span>
                </li>
                <li className="flex items-center" data-id="element-137">
                  <Phone className="w-5 h-5 mr-2 flex-shrink-0 text-purple-400" data-id="element-138" />
                  <span data-id="element-139">786-627-9073</span>
                </li>
                <li className="flex items-center" data-id="element-140">
                  <Mail className="w-5 h-5 mr-2 flex-shrink-0 text-purple-400" data-id="element-141" />
                  <span data-id="element-142">soporte@brixencredit.com</span>
                </li>
              </ul>
            </div>

            {/* Hours */}
            <div data-id="element-143">
              <h4 className="text-lg font-bold mb-6" data-id="element-144">
                {t('footer.hoursTitle')}
              </h4>
              <div className="flex items-start text-sm text-purple-200" data-id="element-145">
                <Clock className="w-5 h-5 mr-2 flex-shrink-0 text-purple-400 mt-0.5" data-id="element-146" />
                <div data-id="element-147">
                  <p className="mb-1" data-id="element-148">Lunes - Sábado</p>
                  <p className="text-white font-semibold mb-3" data-id="element-149">
                    9:00 AM - 9:00 PM EST
                  </p>
                  <p className="text-purple-300" data-id="element-150">Domingo: Cerrado</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-purple-700/50 pt-8 mt-8" data-id="element-151">
            {/* Legal Disclaimer */}
            <div className="mb-8 text-xs text-purple-300 text-center max-w-5xl mx-auto leading-relaxed space-y-3" data-id="element-152">
              <p data-id="element-153">
                Brixen Credit LLC provides credit restoration and document
                preparation services. We are not a law firm, financial
                institution, or direct lender.
              </p>
              <p data-id="element-154">
                In compliance with the Credit Repair Organizations Act (CROA),
                we do not guarantee specific results, the removal of accurate
                and verifiable negative information, or specific credit score
                increases. Individual results vary and are dependent on your
                unique credit profile.
              </p>
              <p data-id="element-155">
                This site is not affiliated with or endorsed by Facebook or Meta
                Platforms, Inc. 'Facebook' and 'Meta' are registered trademarks
                of Meta Platforms, Inc.
              </p>
              <p data-id="element-156">
                Information shared on this website is for informational purposes
                only and does not constitute legal or financial advice. By using
                this site or our services, you agree to our Terms & Conditions
                and Privacy Policy, and consent to receive communications
                (including SMS and calls) from Brixen Credit LLC under
                applicable laws.
              </p>
            </div>

            {/* Copyright and Links */}
            <div className="flex flex-col md:flex-row justify-between items-center pt-6 border-t border-purple-700/50" data-id="element-157">
              <p className="text-xs text-purple-300 text-center md:text-left mb-4 md:mb-0" data-id="element-158">
                © 2026 Brixen Credit LLC. All rights reserved.
              </p>
              <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-xs text-purple-300" data-id="element-159">
                <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors cursor-pointer underline" data-id="element-160">
                  Privacy Policy
                </button>
                <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors cursor-pointer underline" data-id="element-161">
                  Terms & Conditions
                </button>
                <button onClick={() => setActiveModal('refund')} className="hover:text-white transition-colors cursor-pointer underline" data-id="element-162">
                  Refund Policy
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Privacy Policy" content={PRIVACY_POLICY} data-id="element-163" />
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Terms & Conditions" content={TERMS_CONDITIONS} data-id="element-164" />
      <LegalModal isOpen={activeModal === 'refund'} onClose={() => setActiveModal(null)} title="Refund Policy" content={REFUND_POLICY} data-id="element-165" />
    </>;
}